import { useEffect, useState } from "react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BookOpenCheck, Pencil, Save, X } from "lucide-react";

export default function Rules() {
  const { user } = useAuth();
  const [rules, setRules] = useState({ content: "", updated_at: null, updated_by: null });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/rules");
      setRules(data);
      setDraft(data.content);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load rules");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (draft.trim().length < 10) {
      toast.error("Rules content is too short");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/admin/settings/rules", { content: draft });
      toast.success("Rules updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const isAdmin = user && user !== false && user.role === "admin";

  return (
    <>
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="flex items-start gap-3 mb-6">
          <BookOpenCheck className="w-6 h-6 text-[#FF7E67] mt-1" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-[#857163]">Community</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24]">
              House <span className="text-[#FF7E67]">Rules</span>
            </h1>
            <p className="mt-2 text-[#857163]">The recipe for keeping Cake Pop Talk sweet.</p>
          </div>
          {isAdmin && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="cp-btn-ghost inline-flex items-center gap-1.5"
              data-testid="edit-rules-btn"
            >
              <Pencil className="w-4 h-4" /> Edit
            </button>
          )}
        </div>

        {editing ? (
          <div className="cp-card p-5 sm:p-6" data-testid="rules-editor">
            <textarea
              className="cp-input min-h-[420px] font-mono text-sm"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              data-testid="rules-textarea"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={() => { setEditing(false); setDraft(rules.content); }}
                className="cp-btn-ghost inline-flex items-center gap-1.5"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="cp-btn-primary inline-flex items-center gap-1.5 disabled:opacity-70"
                data-testid="save-rules-btn"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save rules"}
              </button>
            </div>
            <div className="mt-3 text-xs text-[#857163]">
              Tip: use blank lines to separate rules. Numbered lines render as sections.
            </div>
          </div>
        ) : (
          <article className="cp-card p-6 sm:p-10" data-testid="rules-view">
            {rules.content.split(/\n\n+/).map((block, i) => {
              const trimmed = block.trim();
              if (!trimmed) return null;
              // Detect "1. Heading\nBody" pattern
              const lines = trimmed.split("\n");
              const first = lines[0];
              const rest = lines.slice(1).join("\n").trim();
              const isRule = /^\d+\.\s+/.test(first);
              return (
                <div key={i} className="mb-6 last:mb-0">
                  {isRule ? (
                    <>
                      <h3 className="font-heading text-xl sm:text-2xl text-[#3B2C24] leading-tight flex items-baseline gap-2">
                        <span className="text-[#FF7E67] font-bold">{first.match(/^(\d+)\./)[1]}.</span>
                        <span>{first.replace(/^\d+\.\s+/, "")}</span>
                      </h3>
                      {rest && <p className="mt-1.5 text-[#3B2C24]/80 leading-relaxed whitespace-pre-wrap">{rest}</p>}
                    </>
                  ) : (
                    <p className="text-[#3B2C24] leading-relaxed whitespace-pre-wrap">{trimmed}</p>
                  )}
                </div>
              );
            })}
            {rules.updated_by && (
              <div className="mt-8 pt-4 border-t border-[#EBDCD1] text-xs text-[#857163]">
                Last updated by @{rules.updated_by}
              </div>
            )}
          </article>
        )}
      </main>
    </>
  );
}
